import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Box } from '@mui/material';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  CATEGORY_SEARCH_PARAM,
  ROUTE_REWARDS_V2_LIST,
  ROUTE_REWARDS_V2_STORE_ID,
  STORE_ID_ROUTE_PARAM,
} from '../../config/routes';
import { POSTHOG_CAPTURE_ATTRIBUTES } from '../../config/tracker';
import LoadingBar from '../../global/LoadingBar';
import CategoryTabs from '../../global/Mui/CategoryTabs';
import CategoryTab from '../../global/Mui/CategoryTab';
import { getCurrentRouteTitle, getSearchParam } from '../../utils/routes';
import { getCategories, getStores } from '../../utils/service';
import { getGenericError } from '../../utils/errors';
import { isProd } from '../../../../config/config';
import { LIST_PAGE_SIZE } from '../../config/config';
import SearchInputButton from '../../global/SearchInputButton/SearchInputButton';
import RewardsSearchV2 from '../../global/RewardsSearchV2';
import RewardsStoreList from '../../global/RewardsStoreList';

import {
  getCategory,
  FIXED_DEALS_CATEGORIES,
} from '../Rewards/utils';
import { pushSelectStoreEvent } from '../../utils/gtm';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';

import './RewardsV2.scss';

const LIST_ELEMENT_ID = 'List';
const DEFAULT_ICON_BACKGROUND = 'var(--trashie-ui-lavander-pastille)';

const ALL_CATEGORY = FIXED_DEALS_CATEGORIES[0];

const RewardsV2 = () => {
  const { search } = useLocation();
  const history = useHistory();
  const { userData } = useFirebaseAuth();
  const timeoutRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [categoryStores, setCategoryStores] = useState([]);
  const [categoryStoresTotal, setCategoryStoresTotal] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStores, setIsLoadingStores] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [storesError, setStoresError] = useState('');
  const [hasLoadedStores, setHasLoadedStores] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const query = useMemo(() => (
    new URLSearchParams(search)
  ), [search]);

  const rewardCategoryParam = useMemo(() => (
    getSearchParam(query, CATEGORY_SEARCH_PARAM)
  ), [query]);

  const scrollToTop = useCallback(() => {
    const listElement = document.getElementById(LIST_ELEMENT_ID);

    if (listElement) {
      timeoutRef.current = setTimeout(() => {
        listElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }, []);

  const loadCategoryStores = useCallback(async (
    routeName,
    newCategories,
  ) => {
    setIsLoadingStores(true);

    const newPage = routeName === selectedCategory.routeName ? page + 1 : 1;

    if (newPage === 1) {
      setCategoryStores([]);
      setCategoryStoresTotal(0);
    }

    const currentTotal = newPage === 1 ? 0 : categoryStoresTotal;
    const currentStores = newPage === 1 ? [] : [...categoryStores];
    const resolvedCategories = newCategories?.length ? newCategories : categories;
    const category = getCategory(resolvedCategories, routeName, [ALL_CATEGORY]);

    const { data, error: categoryStoresError } = await getStores({
      pageSize: LIST_PAGE_SIZE,
      page: newPage,
      categoryId: category?.uid !== ALL_CATEGORY.uid ? category?.uid : undefined,
    });

    if (categoryStoresError) {
      setCategoryStores(currentStores);
      setCategoryStoresTotal(currentTotal);
      setPage(page);
      setStoresError(categoryStoresError?.message || getGenericError());
      setIsLoadingStores(false);
      return;
    }

    setCategoryStores([...currentStores, ...data.stores]);
    setCategoryStoresTotal(data.total);
    setPage(newPage);
    setStoresError('');
    setIsLoadingStores(false);
    setHasLoadedStores(true);
  }, [
    selectedCategory,
    page,
    categories,
    categoryStoresTotal,
    categoryStores,
  ]);

  const loadCategories = useCallback(async () => {
    setIsLoading(true);

    const { data, error: rewardCategoriesError } = await getCategories({ maxExchangeValue: 0 });

    if (rewardCategoriesError) {
      setCategories([]);
      setSelectedCategory({});
      setError(rewardCategoriesError?.message || getGenericError());
      setIsLoading(false);
      return;
    }

    const newCategories = [ALL_CATEGORY, ...data.categories];
    const initialSelectedCategory = getCategory(newCategories, rewardCategoryParam, [ALL_CATEGORY]);

    setCategories(newCategories);
    setSelectedCategory(initialSelectedCategory);
    setError('');
    setIsLoading(false);

    loadCategoryStores(initialSelectedCategory.routeName, newCategories);

    query.set(CATEGORY_SEARCH_PARAM, initialSelectedCategory.routeName);

    history.push({
      pathname: ROUTE_REWARDS_V2_LIST,
      search: query.toString(),
    });
  }, [
    rewardCategoryParam,
    loadCategoryStores,
  ]);

  const handleCategoryChange = useCallback((event, newValue) => {
    if (newValue === selectedCategory.routeName) {
      return;
    }

    const newCategory = getCategory(categories, newValue, [ALL_CATEGORY]);
    setCategoryStores([]);
    setSelectedCategory(newCategory);
    loadCategoryStores(newCategory.routeName);
    scrollToTop();

    if (event.target) {
      query.set(CATEGORY_SEARCH_PARAM, newValue);
      history.push({
        pathname: ROUTE_REWARDS_V2_LIST,
        search: query.toString(),
      });
    }
  }, [
    history,
    query,
    selectedCategory,
    categories,
    loadCategoryStores,
  ]);

  const handleLoadMore = useCallback(() => {
    if (isLoadingStores) return;
    loadCategoryStores(selectedCategory.routeName);
  }, [isLoadingStores, loadCategoryStores, selectedCategory]);

  const handleOnClick = useCallback((store, index) => {
    pushSelectStoreEvent(store, userData, index);
    history.push(ROUTE_REWARDS_V2_STORE_ID.replace(STORE_ID_ROUTE_PARAM, store.id));
  }, [history, userData]);

  useEffect(() => {
    if (hasLoadedStores && rewardCategoryParam.routeName) {
      handleCategoryChange({}, rewardCategoryParam.routeName);
    }
  }, [hasLoadedStores, rewardCategoryParam.routeName]);

  useEffect(() => {
    loadCategories();

    return () => clearTimeout(timeoutRef.current);
  }, []);

  return (
    <div className="RewardsV2View">
      <div className="RewardsV2View__content">
        {error || (isLoading ? (
          <LoadingBar className="RewardsV2View__content--loader" />
        ) : (
          <>
            <Box className={classNames('RewardsV2View__content--controls', { hasEnvBanner: !isProd() })}>
              <Box className="RewardsV2View__content--controls-filter-group">
                <div className="RewardsV2View__content--header">
                  <div>
                    <h2 className="RewardsV2View__content--header-title">
                      {getCurrentRouteTitle()}
                    </h2>
                    <p className="RewardsV2View__content--header-subtitle">
                      Earn more TrashieCash™ when you shop with Deals!
                    </p>
                  </div>
                  <Box className="RewardsV2View__content--controls-options">
                    <div className="RewardsV2View__input-container">
                      <SearchInputButton readOnly onFocus={() => setIsSearching(true)} />
                    </div>
                    {isSearching && (
                      <RewardsSearchV2
                        onClose={() => setIsSearching(false)}
                      />
                    )}
                  </Box>
                </div>
              </Box>
              <CategoryTabs
                className="RewardsV2View__content--controls-tabsV2"
                value={selectedCategory.routeName}
                onChange={handleCategoryChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="store categories"
              >
                {categories.map(({ uid, name, iconUrl, routeName, meta }) => (
                  <CategoryTab
                    key={uid}
                    value={routeName}
                    label={name}
                    iconBackground={meta?.bgColor || DEFAULT_ICON_BACKGROUND}
                    icon={iconUrl ? <img src={iconUrl} alt="tab icon" /> : null}
                  />
                ))}
              </CategoryTabs>
            </Box>
            <Box
              id={LIST_ELEMENT_ID}
              className={classNames('RewardsV2View__content--pane', { hasEnvBanner: !isProd() })}
            >
              {!isLoadingStores && categoryStores.length === 0 ? (
                <div className="RewardsV2View__content--empty">
                  <div className="RewardsV2View__content--empty-title">No stores found</div>
                  <div className="RewardsV2View__content--empty-subtitle">Try a different category.</div>
                </div>
              ) : (
                <InfiniteScroll
                  dataLength={categoryStores.length}
                  next={handleLoadMore}
                  hasMore={categoryStores.length < categoryStoresTotal}
                  className="RewardsV2View__content--scroll"
                >
                  <RewardsStoreList
                    storesList={categoryStores}
                    onClick={handleOnClick}
                    trackerProps={{
                      [POSTHOG_CAPTURE_ATTRIBUTES.SECTION]: 'stores-list',
                      ...(selectedCategory.uid !== ALL_CATEGORY.uid && {
                        [POSTHOG_CAPTURE_ATTRIBUTES.CATEGORY]: selectedCategory.name,
                        [POSTHOG_CAPTURE_ATTRIBUTES.CATEGORY_ID]: selectedCategory.uid,
                      }),
                    }}
                  />
                </InfiniteScroll>
              )}
              {isLoadingStores && (
                <LoadingBar className="RewardsV2View__content--loader" />
              )}
              {storesError && storesError}
            </Box>
          </>
        ))}
      </div>
    </div>
  );
};

export default RewardsV2;
